//! Utility to run api

import * as StoreSelectors from '../redux/selectors';
import { doCorsCheck } from '../redux/actions/ui.actions';
import config from '../cfg';
const { api_url, legacy_api_url } = config;
import { testApiAccess } from './csp-detector';
// Initialize the test promise immediately when script loads
let apiAccessTestPromise = null;
if (typeof window !== "undefined") {
  apiAccessTestPromise = testApiAccess(api_url, legacy_api_url);
}
import { Buffer } from 'buffer';

// Handling requests

export function getHeaders() {
  const headers = {
    'x-api-key': StoreSelectors.getApiKey(),
    'Content-Type': 'application/json',
  };


  const buildType = import.meta.env.WIDGET_BUILD_TYPE;
  const widgetVersion = import.meta.env.WIDGET_VERSION;
  
  if (buildType === 'vanilla') {
    headers['hg-request-source'] = 'widget-v2-vanilla';
  } else if (buildType === 'react' && widgetVersion) {
    headers['hg-request-source'] = `widget-v2-${widgetVersion}`;
  }

  return headers;
}

class ExtendableError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}

class APIError extends ExtendableError {
  constructor(m, errorObj) {
    super(m);
    this.errorObj = errorObj;
  }

  errorObj = null;
}

const doRequest = async (url, body = {}, options = { env: true }) => {

  // Wait for API access test to complete
  if (apiAccessTestPromise) {
    await apiAccessTestPromise;
    apiAccessTestPromise = null;
  }

  body.headers = { ...getHeaders(), ...body.headers };
  // delete any undefined headers
  Object.keys(body.headers).forEach((key) => (body.headers[key] === undefined ? delete body.headers[key] : {}));

  // Use alternate URL if the main API is blocked
  const _api_url = window.hotglue_widget_useAlternateUri ? legacy_api_url : api_url;

  if (options.env) {
    url = `${StoreSelectors.getApiUrl() || _api_url}/${StoreSelectors.getEnvId()}/${url}`;
  } else {
    url = `${StoreSelectors.getApiUrl() || _api_url}/${url}`;
  }
  
  const jwtToken = StoreSelectors.getJwtToken();
  if (jwtToken) {
    url += (url.includes('?') ? '&' : '?') + `token=${jwtToken}`;
  }

  const response = await new Promise((resolve) => {
    fetch(url, body)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        if (!StoreSelectors.corsCheck()) {
          // Marks CORS Check
          doCorsCheck(true);

          // Alert if there is no override in listener
          const listener = StoreSelectors.getListener();
          if (listener && listener.onNetworkError) {
            listener.onNetworkError();
          } else {
            console.error('An error occurred while making an API request. Do you have an adblocker enabled?');
          }
        }
      });
  });

  if (response.status !== 200) {
    const sendUserError = async () => {
      if (response) {
        const { Code, Message } = await response.json();
        if (Code && Message) {
          throw new APIError(Message.replace('ChaliceViewError:', ''), response.data);
        } else throw new APIError(response.statusText, response.data);
      } else {
        throw new Error(response.statusText);
      }
    };

    return sendUserError();
  }

  return response.json();
};

const doRequestV2 = async (url, body = {}, options = { env: true }) => {

  // Wait for API access test to complete
  if (apiAccessTestPromise) {
    await apiAccessTestPromise;
    apiAccessTestPromise = null;
  }

  body.headers = { ...getHeaders(), ...body.headers };
  // delete any undefined headers
  Object.keys(body.headers).forEach((key) => (body.headers[key] === undefined ? delete body.headers[key] : {}));

  // Use alternate URL if the main API is blocked
  const _api_url = window.hotglue_widget_useAlternateUri ? legacy_api_url : api_url;

  if (options.env) {
    url = `${StoreSelectors.getApiUrl() || _api_url}/v2/${StoreSelectors.getEnvId()}/${url}`;
  } else {
    url = `${StoreSelectors.getApiUrl() || _api_url}/v2/${url}`;
  }

  const jwtToken = StoreSelectors.getJwtToken();
  if(jwtToken) {
    url += (url.includes('?') ? '&' : '?') + `token=${jwtToken}`;
  }  

  const response = await new Promise((resolve) => {
    fetch(url, body)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        if (!StoreSelectors.corsCheck()) {
          // Marks CORS Check
          doCorsCheck(true);

          // Alert if there is no override in listener
          const listener = StoreSelectors.getListener();
          if (listener && listener.onNetworkError) {
            listener.onNetworkError();
          } else {
            console.error('An error occurred while making an API request. Do you have an adblocker enabled?');
          }
        }
      });
  });

  if (response.status !== 200) {
    const sendUserError = async () => {
      if (response) {
        const { Code, Message } = await response.json();
        if (Code && Message) {
          throw new APIError(Message.replace('ChaliceViewError:', ''), response.data);
        } else throw new APIError(response.statusText, response.data);
      } else {
        throw new Error(response.statusText);
      }
    };

    return sendUserError();
  }

  return response.json();
};

export const Request = {
  GET: (url, body, options) => doRequest(url, { ...{ method: 'GET' }, ...body }, options),
  DELETE: (url, body) => doRequest(url, { ...{ method: 'DELETE' }, ...body }),
  POST: (url, body, options) => doRequest(url, { ...{ method: 'POST' }, ...body }, options),
  PUT: (url, body, options) => doRequest(url, { ...{ method: 'PUT' }, ...body }, options),
  PATCH: (url, body, options) => doRequest(url, {...{ method: 'PATCH' }, ...body}, options)
};

export const RequestV2 = {
  GET: (url, body, options) => doRequestV2(url, { ...{ method: 'GET' }, ...body }, options),
  DELETE: (url, body) => doRequestV2(url, { ...{ method: 'DELETE' }, ...body }),
  POST: (url, body, options) => doRequestV2(url, { ...{ method: 'POST' }, ...body }, options),
  PUT: (url, body, options) => doRequestV2(url, { ...{ method: 'PUT' }, ...body }, options),
  PATCH: (url, body, options) => doRequestV2(url, {...{ method: 'PATCH' }, ...body}, options)
};
